import React, { useContext, useEffect, useMemo } from "react";
import Joyride, { ACTIONS, EVENTS, STATUS } from "react-joyride";
import { useLocation } from "react-router-dom";
import { TourContext } from "../contexts/TourContext";
import { mainChatSteps, collectionManagementSteps, userManagementSteps } from "./tourSteps";
import TourTooltip from "./TourTooltip";
import { joyrideStyles } from "./tourStyles";

const stepMap = {
  mainChat: mainChatSteps,
  collectionManagement: collectionManagementSteps,
  userManagement: userManagementSteps,
};

// Error boundary to prevent Joyride crashes (e.g. MUI pulsate/ripple conflicts)
// from taking down the entire app
class TourErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error, info) {
    console.warn("Tour error caught:", error.message);
    if (this.props.onError) {
      this.props.onError();
    }
  }

  componentDidUpdate(prevProps) {
    // Reset error state when tour restarts
    if (this.props.tourKey !== prevProps.tourKey && this.state.hasError) {
      this.setState({ hasError: false });
    }
  }

  render() {
    if (this.state.hasError) return null;
    return this.props.children;
  }
}

const TourOverlay = () => {
  const {
    activeTour,
    stepIndex,
    isTourRunning,
    endTour,
    goToStep,
    invokeCallback,
  } = useContext(TourContext);

  const location = useLocation();

  // End tour if user navigates away from the expected page
  useEffect(() => {
    if (
      activeTour === "mainChat" &&
      !location.pathname.startsWith("/main-chat")
    ) {
      endTour();
    }
    if (
      activeTour === "collectionManagement" &&
      !location.pathname.startsWith("/collections")
    ) {
      endTour();
    }
    if (
      activeTour === "userManagement" &&
      !location.pathname.startsWith("/user-management")
    ) {
      endTour();
    }
  }, [location.pathname, activeTour, endTour]);

  const allSteps = activeTour ? stepMap[activeTour] : [];

  // Filter out steps whose target elements don't exist in the DOM
  // (e.g., Management link only appears for admin users)
  // Skip filtering for steps with programmatic actions (their targets get created mid-tour)
  const steps = useMemo(() => {
    return allSteps.filter((step) => {
      if (step.data?.action) return true;
      if (typeof step.target === "string") {
        return document.querySelector(step.target) !== null;
      }
      return true;
    });
  }, [allSteps, activeTour, isTourRunning]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleCallback = (data) => {
    const { action, index, status, type, step } = data;

    // Pre-step actions — immediate, no DOM wait
    if (type === EVENTS.STEP_BEFORE) {
      if (step.data?.action === "closeSidebar") {
        invokeCallback("closeSidebar");
      }
      if (step.data?.action === "markQuickChatIntroSeen") {
        invokeCallback("markQuickChatIntroSeen");
      }
    }

    // Target not found — retry for steps that need the sidebar to render
    if (type === EVENTS.TARGET_NOT_FOUND) {
      if (step.data?.action === "openSidebar") {
        invokeCallback("openSidebar");
      }
      return;
    }

    // Navigation
    if (type === EVENTS.STEP_AFTER) {
      if (action === ACTIONS.CLOSE) {
        endTour();
        invokeCallback("closeSidebar");
        return;
      }

      const nextIndex =
        action === ACTIONS.NEXT
          ? index + 1
          : action === ACTIONS.PREV
          ? index - 1
          : null;

      if (nextIndex !== null && nextIndex >= 0 && nextIndex < steps.length) {
        const nextStep = steps[nextIndex];

        // If the next step needs the sidebar open, open it first and wait for render
        if (nextStep?.data?.action === "openSidebar") {
          invokeCallback("openSidebar");
          setTimeout(() => goToStep(nextIndex), 450);
          return;
        }

        goToStep(nextIndex);
      } else if (action === ACTIONS.NEXT) {
        // Past the last step — finish the tour
        endTour();
        invokeCallback("closeSidebar");
        return;
      }
    }

    // Completion or skip
    if (status === STATUS.FINISHED || status === STATUS.SKIPPED) {
      endTour();
      invokeCallback("closeSidebar");
    }
  };

  if (!activeTour || steps.length === 0) return null;

  return (
    <TourErrorBoundary onError={endTour} tourKey={`${activeTour}-${stepIndex}`}>
      <Joyride
        steps={steps}
        stepIndex={stepIndex}
        run={isTourRunning}
        continuous
        showSkipButton
        showProgress={false}
        disableOverlayClose
        disableCloseOnEsc={false}
        spotlightClicks={false}
        disableScrolling
        scrollOffset={80}
        callback={handleCallback}
        tooltipComponent={TourTooltip}
        styles={joyrideStyles}
        locale={{
          back: "Back",
          close: "Close",
          last: "Finish",
          next: "Next",
          skip: "Skip Tour",
        }}
        floaterProps={{
          disableAnimation: false,
          styles: {
            arrow: {
              color: "#1a1a1a",
            },
          },
        }}
      />
    </TourErrorBoundary>
  );
};

export default TourOverlay;
